<template>
  <section class="flow-text text container">
    <h1>About Retribute</h1>
    <p>
      Retribute is a web application designed to make the job of supporting the creators you like easier.
    </p>

    <h3>How does it work?</h3>
    <p>
      By <router-link to="/connect">connecting your existing accounts</router-link> on other platforms to Retribute, you will receive suggestions
      of creators you may want to support, as well as the donation services where you can support them.
    </p>
    <p>
      Those suggestions based on your interactions with their content, and your interactions only. The more interactions you have,
      the higher the creator will appear in suggestions.
    </p>
    <p>
      We don't use the creator general popularity or other aggregated data to perform the ranking.
    </p>

    <h3>What donations services are supported?</h3>
    <p>
      We will successfully retrieve links and informations for the following services:
    </p>
    <ul v-if="providers" class="browser-default">
      <li v-for="provider in providers" :key="provider.id">
        <a :href="provider.url" target="_blank" rel="noopener">{{ provider.label }}</a>
      </li>
    </ul>

    <p>If you would like to add a service to this list, <a href="#contact">please let us know!</a></p>
    <p>
      In addition to the platforms listed above, any URL found in creators profiles with a <code>?retribute</code> query parameter will be collected and displayed as well.
      As a creator, you can leverage this feature to advertise custom retribution platforms, e.g your personal e-shop, or your Amazon wishlist.
    </p>

    <p>
      Example: <code>https://mysite.com/wishlist?retribute</code>
    </p>

    <h3>How do you retrieve donation information?</h3>
    <p>
      We extract links to donation services from creators profiles. On Mastodon, we use the profile metadata and bio.
    </p>
    <p>If you are on Mastodon and want your audience to support you via Retribute, ensure your profile include at least a link to one of the donation services listed above.</p>
    <p>Profiles are cached for a few hours, so the change may take some time before being reflected on Retribute side.</p>

    <h3>What content platforms are supported?</h3>
    <p>
      You can connect your accounts on the following platforms to Retribute:
    </p>
    <ul v-if="sources" class="browser-default">
      <li v-if="source.connect" v-for="source in sources" :key="source.id">
        <a :href="source.url" target="_blank" rel="noopener">{{ source.label }}</a>
      </li>
    </ul>
    <p>If you would like to add a service to this list, <a href="#contact">please let us know!</a></p>

    <h3>What data do you store?</h3>
    <p>All the private/sensitive data is processed and stored whithin your web browser local storage only, especially:</p>
    <ul class="browser-default">
      <li>Application credentials</li>
      <li>Suggestions</li>
      <li>Data retrieved from connected accounts</li>
    </ul>
    <p>You can erase this data by clearing your browser cache, or clicking on the button below:</p>
    <button class="waves-effect waves-light btn modal-trigger" href="#reset-modal">Forget local data…</button>
    <div id="reset-modal" class="modal">
      <div class="modal-content">
        <h4>Do you want to erase your local data?</h4>
        <p>This will remove any credential, suggestion and connected account data from your browser cache. This action is irreversible.</p>
      </div>
      <div class="modal-footer">
        <a href="#!" class="modal-close waves-effect waves-green btn-flat">Cancel</a>
        <a @click="reset" href="#!" class="modal-close waves-effect waves-green btn-flat">Confirm</a>
      </div>
    </div>
    <p>To retrieve donation information for creators, we query a separate service. This service doesn't keep any personal data, and only store webserver logs for a few weeks.</p>

    <h3>Useful links</h3>
    <ul class="browser-default">
      <li>
        <a href="https://dev.funkwhale.audio/retribute.me/webclient/issues" target="_blank" rel="noopener">Issue tracker</a>
      </li>
      <li>
        <a href="https://dev.funkwhale.audio/retribute.me" target="_blank" rel="noopener">Source code</a>
      </li>
    </ul>

    <h3 id="contact">Contact information</h3>
    <p>
      This service is maintained by <a href="https://eliotberriot.com">Eliot Berriot</a>. Contact me by email at <a href="mailto:contact@eliotberriot.com">contact@eliotberriot.com</a>
      if you have any question, or open an issue on our <a href="https://dev.funkwhale.audio/retribute.me/webclient/issues" target="_blank" rel="noopener">Issue tracker</a>.
    </p>

  </section>
</template>
<script>
import axios from 'axios'
import config from "@/config"
import sources from "@/sources"
import sortBy from 'lodash/sortBy'

export default {
  data () {
    return {
      providers: null
    }
  },
  async created () {
    await this.fetch()
  },
  mounted () {
    var elems = document.querySelectorAll('.modal')
    M.Modal.init(elems, {
      onCloseEnd () {
        // cf https://github.com/Dogfalo/materialize/issues/4622#issuecomment-298072452
        document.body.style.removeProperty("overflow");
      }
    })
  },
  computed: {
    sources () {
      return sortBy(Object.values(sources.sources), 'id')
    }
  },
  methods: {
    async fetch () {
      const client = axios.create()
      let url = config.RetributeAPIUrl + `v1/providers`
      const response = await axios.get(url)
      this.providers = response.data
    },
    reset () {
      this.$store.commit('reset')
    }
  }
}
</script>
